import { useState } from "react";
import { Archive, FileKey, KeyRound } from "lucide-react";
import { toast } from "sonner";
import {
  useGenerateCsrForSubscriber,
  useGenerateP12ForSubscriber,
  useGeneratePfxForSubscriber,
} from "@/hooks/queries";
import { ApiError } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { Field, Input, Textarea } from "@/components/ui/Input";
import { SecretField } from "@/components/ui/SecretField";
import { base64ToBytes, downloadFile, formatDate } from "@/lib/utils";
import type {
  GenerateCsrForSubscriberResponse,
  GenerateP12ForSubscriberResponse,
  GeneratePfxForSubscriberResponse,
} from "@/types";

export function GenerateTab({ subscriberId }: { subscriberId: string }) {
  const genCsr = useGenerateCsrForSubscriber(subscriberId);
  const genPfx = useGeneratePfxForSubscriber(subscriberId);
  const genP12 = useGenerateP12ForSubscriber(subscriberId);

  const [csrResult, setCsrResult] = useState<GenerateCsrForSubscriberResponse | null>(null);
  const [pem, setPem] = useState("");
  const [pfxResult, setPfxResult] = useState<GeneratePfxForSubscriberResponse | null>(null);
  const [p12Alias, setP12Alias] = useState("");
  const [p12Result, setP12Result] = useState<GenerateP12ForSubscriberResponse | null>(null);

  async function onGenerateCsr() {
    try {
      const res = await genCsr.mutateAsync();
      setCsrResult(res);
      toast.success("CSR generated");
    } catch (err) {
      if (err instanceof ApiError) toast.error(`[${err.code}] ${err.message}`);
      else toast.error("Failed to generate CSR");
    }
  }

  async function onGeneratePfx(e: React.FormEvent) {
    e.preventDefault();
    try {
      const res = await genPfx.mutateAsync({ pem });
      setPfxResult(res);
      toast.success("PFX generated");
    } catch (err) {
      if (err instanceof ApiError) toast.error(`[${err.code}] ${err.message}`);
      else toast.error("Failed to generate PFX");
    }
  }

  function downloadPfx(result: GeneratePfxForSubscriberResponse) {
    downloadFile(result.pfxFileName, base64ToBytes(result.pfx), result.pfxContentType);
    toast.success(`Downloaded ${result.pfxFileName}`);
  }

  async function onGenerateP12(e: React.FormEvent) {
    e.preventDefault();
    try {
      const trimmed = p12Alias.trim();
      const res = await genP12.mutateAsync(trimmed ? { alias: trimmed } : {});
      setP12Result(res);
      toast.success("P12 generated");
    } catch (err) {
      if (err instanceof ApiError) toast.error(`[${err.code}] ${err.message}`);
      else toast.error("Failed to generate P12");
    }
  }

  function downloadP12(result: GenerateP12ForSubscriberResponse) {
    downloadFile(result.p12FileName, base64ToBytes(result.p12), result.p12ContentType);
    toast.success(`Downloaded ${result.p12FileName}`);
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-brand-600" />
            <CardTitle>Step 1 · Generate CSR</CardTitle>
          </div>
          <CardDescription>
            Builds an RSA 2048 CSR from this subscriber&apos;s registration data. The private key
            is stored encrypted as an artifact on this subscriber. Click again to regenerate: Andes
            rejects a CSR that is already used on another solicitud, so a new PEM is required.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button onClick={onGenerateCsr} loading={genCsr.isPending}>
            Generate CSR / regenerate
          </Button>
          {csrResult && (
            <div className="space-y-3 pt-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                <div>
                  <p className="text-slate-400">Subject</p>
                  <p className="text-slate-900 font-mono break-all">{csrResult.subject}</p>
                </div>
                <div>
                  <p className="text-slate-400">Algorithm / Key size</p>
                  <p className="text-slate-900">
                    {csrResult.algorithm} · {csrResult.keySize} bits
                  </p>
                </div>
                <div>
                  <p className="text-slate-400">Created</p>
                  <p className="text-slate-900">{formatDate(csrResult.createdAt)}</p>
                </div>
              </div>
              <SecretField
                label="CSR (submit to Andes SCD)"
                value={csrResult.csr}
                revealDefault
                multiline
                download={{ filename: "request.csr", mime: "application/pkcs10" }}
              />
              <SecretField
                label="Private key (also stored encrypted)"
                value={csrResult.key}
                multiline
                download={{ filename: "private-key.pem", mime: "application/x-pem-file" }}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <FileKey className="h-4 w-4 text-brand-600" />
            <CardTitle>Step 2 · Generate PFX</CardTitle>
          </div>
          <CardDescription>
            Paste the signed PEM certificate from Andes SCD. The stored private key is combined
            with the PEM to produce a PFX. A strong password is auto-generated and returned once —
            the PFX, password, and PEM are all stored encrypted.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onGeneratePfx} className="space-y-3">
            <Field label="Signed PEM certificate" required>
              <Textarea
                rows={10}
                value={pem}
                onChange={(e) => setPem(e.target.value)}
                placeholder="-----BEGIN CERTIFICATE-----&#10;…&#10;-----END CERTIFICATE-----"
                maxLength={16_000}
                required
              />
            </Field>
            <Button type="submit" loading={genPfx.isPending} disabled={!pem.trim()}>
              Generate PFX
            </Button>
          </form>

          {pfxResult && (
            <div className="space-y-3 pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                <div>
                  <p className="text-slate-400">Friendly name</p>
                  <p className="text-slate-900">{pfxResult.friendlyName}</p>
                </div>
                <div>
                  <p className="text-slate-400">Serial</p>
                  <p className="text-slate-900 font-mono break-all">{pfxResult.serialNumber}</p>
                </div>
                <div>
                  <p className="text-slate-400">Issuer</p>
                  <p className="text-slate-900">{pfxResult.issuer}</p>
                </div>
                <div>
                  <p className="text-slate-400">Subject</p>
                  <p className="text-slate-900 break-all">{pfxResult.subject}</p>
                </div>
                <div>
                  <p className="text-slate-400">Valid from</p>
                  <p className="text-slate-900">{formatDate(pfxResult.validFrom)}</p>
                </div>
                <div>
                  <p className="text-slate-400">Valid to</p>
                  <p className="text-slate-900">{formatDate(pfxResult.validTo)}</p>
                </div>
              </div>
              <SecretField label="PFX password" value={pfxResult.pfxPassword} />
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => downloadPfx(pfxResult)}>
                  Download {pfxResult.pfxFileName}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Archive className="h-4 w-4 text-brand-600" />
            <CardTitle>Step 3 · Generate P12</CardTitle>
          </div>
          <CardDescription>
            Builds a PKCS#12 (.p12) keystore from the stored private key and PEM certificate.
            Requires a PFX to already exist on this subscriber. A fresh password is auto-generated.
            Both the P12 and its password are stored encrypted.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onGenerateP12} className="space-y-3">
            <Field
              label="Keystore alias (optional)"
              hint="Defaults to pahcertificar-{document number} if blank"
            >
              <Input
                type="text"
                value={p12Alias}
                onChange={(e) => setP12Alias(e.target.value)}
                placeholder="pahcertificar-901592460"
                maxLength={200}
              />
            </Field>
            <Button type="submit" loading={genP12.isPending}>
              Generate P12
            </Button>
          </form>

          {p12Result && (
            <div className="space-y-3 pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                <div>
                  <p className="text-slate-400">Alias</p>
                  <p className="text-slate-900 break-all">{p12Result.alias}</p>
                </div>
                <div>
                  <p className="text-slate-400">Friendly name</p>
                  <p className="text-slate-900">{p12Result.friendlyName}</p>
                </div>
                <div>
                  <p className="text-slate-400">Serial</p>
                  <p className="text-slate-900 font-mono break-all">{p12Result.serialNumber}</p>
                </div>
                <div>
                  <p className="text-slate-400">Issuer</p>
                  <p className="text-slate-900">{p12Result.issuer}</p>
                </div>
                <div>
                  <p className="text-slate-400">Subject</p>
                  <p className="text-slate-900 break-all">{p12Result.subject}</p>
                </div>
                <div>
                  <p className="text-slate-400">Valid from</p>
                  <p className="text-slate-900">{formatDate(p12Result.validFrom)}</p>
                </div>
                <div>
                  <p className="text-slate-400">Valid to</p>
                  <p className="text-slate-900">{formatDate(p12Result.validTo)}</p>
                </div>
              </div>
              <SecretField label="P12 password" value={p12Result.p12Password} />
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => downloadP12(p12Result)}>
                  Download {p12Result.p12FileName}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
